import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Store, Tag, Trash2, Check, X, Send, MapPin, Sparkles, Loader2, RefreshCw, Clock } from "lucide-react";
import { BrutalButton, BrutalCard, SectionTitle, Pill, EmptyState } from "@/components/Brutal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MultiPhotoInput } from "@/components/AiComponents";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmProvider";
import { useModuleGuard } from "@/lib/useModuleGuard";
import { useT } from "@/lib/i18n";
import { Tip } from "@/components/Tip";
import api from "@/lib/api";
import { toast } from "sonner";

const TYPES = [
  { key: "huur", label: "Huur", color: "bg-brand-blue text-white" },
  { key: "koop", label: "Koop", color: "bg-brand-violet text-white" },
  { key: "leen", label: "Leen", color: "bg-brand-green" },
  { key: "gratis", label: "Gratis", color: "bg-brand-yellow" },
];
const typeMeta = (k) => TYPES.find((t) => t.key === k) || TYPES[3];
const euro = (c) => "€ " + ((c || 0) / 100).toFixed(2).replace(".", ",");
const priceLabel = (it) => {
  if (it.type === "gratis") return "Gratis";
  if (it.type === "leen") return "Te leen";
  if (it.type === "huur") return `${euro(it.price)}${it.rent_period ? " / " + it.rent_period : ""}`;
  return euro(it.price);
};
const STATUS = { beschikbaar: "bg-brand-green", gereserveerd: "bg-brand-yellow", afgerond: "bg-brand-ink/10" };
const empty = { title: "", description: "", category: "", type: "gratis", price: "", rent_period: "dag", images: [], location: "" };

export default function Hoppa() {
  useModuleGuard("hoppa");
  const { user } = useAuth();
  const tr = useT();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("alle");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [detail, setDetail] = useState(null);
  const [reqMsg, setReqMsg] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const genDesc = async () => {
    if (!form.title.trim()) return toast.error(tr("Vul eerst een titel in"));
    const soort = { gratis: "gratis weggeven", koop: "te koop", huur: "te huur", leen: "uitlenen" }[form.type] || form.type;
    const facts = `Titel: ${form.title}. Soort: ${soort}.` +
      (form.category ? ` Categorie: ${form.category}.` : "") +
      (["koop", "huur"].includes(form.type) && form.price ? ` Prijs: €${form.price}${form.type === "huur" ? " per " + form.rent_period : ""}.` : "") +
      (form.location ? ` Ophaallocatie: ${form.location}.` : "") +
      (form.description ? ` Huidige tekst: ${form.description}` : "");
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/rewrite", { text: facts, context: "Dit is een aanbod op de interne huishoud-marktplaats 'Hoppa'. Schrijf een korte, aantrekkelijke en vriendelijke productomschrijving van 2 tot 3 zinnen in het Nederlands." });
      setForm((f) => ({ ...f, description: data.text }));
      toast.success(tr("Omschrijving gegenereerd ✨"));
    } catch { toast.error(tr("AI-generatie mislukt")); }
    finally { setAiBusy(false); }
  };

  const load = async () => setItems((await api.get("/hoppa")).data);
  useEffect(() => { load(); }, []);

  const shown = filter === "alle" ? items : items.filter((i) => i.type === filter);

  const create = async () => {
    if (!form.title.trim()) return toast.error(tr("Titel is verplicht"));
    const payload = {
      title: form.title, description: form.description || null, category: form.category || null,
      type: form.type, images: form.images.filter(Boolean), location: form.location || null,
      price: ["koop", "huur"].includes(form.type) && form.price ? Math.round(Number(form.price) * 100) : null,
      rent_period: form.type === "huur" ? form.rent_period : null,
    };
    await api.post("/hoppa", payload);
    toast.success(tr("Aanbod geplaatst"));
    setOpen(false); setForm(empty); load();
  };

  const doRequest = async () => {
    await api.post(`/hoppa/${detail.id}/request`, { message: reqMsg || null });
    toast.success("Aanvraag verstuurd 🙌"); setReqMsg("");
    setDetail(null); load();
  };
  const accept = async (reqId) => { const { data } = await api.post(`/hoppa/${detail.id}/requests/${reqId}/accept`); setDetail(data); toast.success("Geaccepteerd"); load(); };
  const setStatus = async (s) => { const { data } = await api.put(`/hoppa/${detail.id}/status`, { status: s }); setDetail(data); load(); };
  const del = async (id) => { if (!(await confirm({ title: "Aanbod verwijderen?" }))) return; await api.delete(`/hoppa/${id}`); toast.success("Verwijderd"); setDetail(null); load(); };
  const renew = async () => { const { data } = await api.post(`/hoppa/${detail.id}/renew`); setDetail(data); load(); toast.success("Verlengd met 8 weken ✅"); };
  const daysLeft = (iso) => iso ? Math.ceil((new Date(iso) - new Date()) / 86400000) : null;

  return (
    <div className="space-y-5" data-testid="hoppa-page">
      <div className="flex items-center justify-between">
        <SectionTitle sub={tr("Huren, kopen, lenen of gratis — voor het hele huis")}>{tr("Hoppa")}</SectionTitle>
        <Tip label={tr("Iets aanbieden")}><BrutalButton data-testid="hoppa-add-btn" onClick={() => { setForm(empty); setOpen(true); }}><Plus className="w-5 h-5" strokeWidth={3} /></BrutalButton></Tip>
      </div>

      <div className="flex flex-wrap gap-2">
        {["alle", ...TYPES.map((t) => t.key)].map((k) => (
          <button key={k} data-testid={`hoppa-filter-${k}`} onClick={() => setFilter(k)}
            className={`brutal-sm brutal-press rounded-full px-4 py-1.5 text-sm font-bold capitalize ${filter === k ? (k === "alle" ? "bg-brand-ink text-white" : typeMeta(k).color) : "bg-white"}`}>
            {k === "alle" ? tr("Alles") : tr(typeMeta(k).label)}
          </button>
        ))}
      </div>

      {shown.length === 0 && <EmptyState icon={Store} title={tr("Nog niks te vinden")} text={tr("Plaats het eerste aanbod met de +.")} />}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {shown.map((it) => (
          <button key={it.id} data-testid={`hoppa-card-${it.id}`} onClick={() => { setDetail(it); setReqMsg(""); }}
            className="brutal brutal-press rounded-xl bg-white overflow-hidden text-left flex flex-col">
            <div className="aspect-[4/3] bg-brand-cream flex items-center justify-center overflow-hidden">
              {it.images?.[0] ? <img src={it.images[0]} alt={it.title} className="w-full h-full object-cover" /> : <Store className="w-10 h-10 text-brand-ink/20" strokeWidth={2} />}
            </div>
            <div className="p-3 flex-1 flex flex-col gap-1">
              <div className="flex items-center gap-1.5">
                <span className={`brutal-sm rounded-full px-2 py-0.5 text-[11px] font-bold ${typeMeta(it.type).color}`}>{tr(typeMeta(it.type).label)}</span>
                {it.status !== "beschikbaar" && <span className={`brutal-sm rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS[it.status]}`}>{tr(it.status)}</span>}
              </div>
              <p className="font-head font-bold leading-tight line-clamp-2">{it.title}</p>
              <p className="font-head font-black text-brand-violet mt-auto">{priceLabel(it)}</p>
              <p className="text-xs text-brand-ink/50 font-semibold">{it.owner_name}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Aanbieden */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[88vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-head font-black text-xl">{tr("Iets aanbieden")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-bold">{tr("Foto's (max 3)")}</label>
              <div className="mt-1"><MultiPhotoInput value={form.images} onChange={(imgs) => setForm((f) => ({ ...f, images: imgs }))} max={3} testid="hoppa-photos" /></div>
            </div>
            <input data-testid="hoppa-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={tr("Wat bied je aan?")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
            <div className="grid grid-cols-4 gap-2">
              {TYPES.map((t) => (
                <button key={t.key} type="button" data-testid={`hoppa-type-${t.key}`} onClick={() => setForm({ ...form, type: t.key })}
                  className={`brutal-sm brutal-press rounded-xl py-2 text-sm font-bold ${form.type === t.key ? t.color : "bg-white"}`}>{tr(t.label)}</button>
              ))}
            </div>
            {["koop", "huur"].includes(form.type) && (
              <div className="flex gap-2">
                <input data-testid="hoppa-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder={tr("Prijs in €")} className="flex-1 min-w-0 brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
                {form.type === "huur" && (
                  <select data-testid="hoppa-period" value={form.rent_period} onChange={(e) => setForm({ ...form, rent_period: e.target.value })} className="brutal-sm rounded-xl px-3 font-bold outline-none bg-white">
                    <option value="dag">{tr("per dag")}</option><option value="week">{tr("per week")}</option>
                  </select>
                )}
              </div>
            )}
            <input data-testid="hoppa-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder={tr("Categorie (bijv. Gereedschap, Meubels)")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-bold">{tr("Omschrijving")}</span>
                <button type="button" data-testid="hoppa-ai-desc" onClick={genDesc} disabled={aiBusy}
                  className="brutal-sm brutal-press rounded-full px-2.5 py-1 text-xs font-bold bg-brand-violet text-white flex items-center gap-1">
                  {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />}{tr("Genereer met AI")}
                </button>
              </div>
              <textarea data-testid="hoppa-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder={tr("Omschrijving — of laat AI 'm schrijven op basis van je titel")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
            </div>
            <input data-testid="hoppa-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder={tr("Ophaal-locatie (optioneel)")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
            <BrutalButton data-testid="hoppa-submit" onClick={create} className="w-full">{tr("Plaatsen")}</BrutalButton>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[88vh] overflow-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-head font-black text-xl">{detail.title}</DialogTitle></DialogHeader>
              {detail.images?.length > 0 && (
                <div className="flex gap-2 overflow-auto">{detail.images.map((im, i) => <img key={i} src={im} alt="" className="h-40 rounded-xl object-cover brutal-sm" />)}</div>
              )}
              <div className="flex items-center gap-2">
                <span className={`brutal-sm rounded-full px-2.5 py-0.5 text-xs font-bold ${typeMeta(detail.type).color}`}>{tr(typeMeta(detail.type).label)}</span>
                <span className="font-head font-black text-brand-violet">{priceLabel(detail)}</span>
                <span className={`brutal-sm rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS[detail.status]}`}>{tr(detail.status)}</span>
              </div>
              {detail.description && <p className="font-medium">{detail.description}</p>}
              {detail.location && <p className="text-sm text-brand-ink/60 font-semibold flex items-center gap-1"><MapPin className="w-4 h-4" strokeWidth={2.5} />{detail.location}</p>}
              <p className="text-sm text-brand-ink/60 font-semibold">{tr("Aangeboden door")} {detail.owner_name}</p>
              {detail.expires_at && (() => { const dl = daysLeft(detail.expires_at); return (
                <p className={`text-xs font-bold flex items-center gap-1 ${dl > 0 ? "text-brand-ink/50" : "text-brand-pink"}`}><Clock className="w-3.5 h-3.5" strokeWidth={2.5} />{dl > 0 ? `${tr("Nog")} ${dl} ${tr("dag(en) online")}` : tr("Verlopen — verleng om zichtbaar te blijven")}</p>
              ); })()}

              {detail.owner_id === user.user_id ? (
                <div className="space-y-2">
                  <p className="font-head font-bold text-sm">{tr("Aanvragen")} ({detail.requests?.length || 0})</p>
                  {(detail.requests || []).length === 0 && <p className="text-sm text-brand-ink/40">{tr("Nog geen aanvragen.")}</p>}
                  {(detail.requests || []).map((r) => (
                    <div key={r.id} data-testid={`hoppa-req-${r.id}`} className="brutal-sm rounded-xl bg-brand-cream p-2.5 flex items-center gap-2">
                      <div className="flex-1"><p className="font-bold text-sm">{r.user_name}</p>{r.message && <p className="text-xs text-brand-ink/60">{r.message}</p>}</div>
                      {r.status === "pending"
                        ? <BrutalButton data-testid={`hoppa-accept-${r.id}`} variant="green" size="sm" onClick={() => accept(r.id)}><Check className="w-4 h-4" strokeWidth={3} /></BrutalButton>
                        : <Pill className={r.status === "accepted" ? "bg-brand-green" : "bg-white"}>{r.status}</Pill>}
                    </div>
                  ))}
                  <div className="flex gap-2 pt-1">
                    {["beschikbaar", "gereserveerd", "afgerond"].map((s) => (
                      <button key={s} data-testid={`hoppa-status-${s}`} onClick={() => setStatus(s)} className={`brutal-sm brutal-press rounded-full px-3 py-1 text-xs font-bold capitalize ${detail.status === s ? STATUS[s] : "bg-white"}`}>{tr(s)}</button>
                    ))}
                    <Tip label={tr("Verleng 8 weken")}><button data-testid="hoppa-renew" onClick={renew} className="brutal-sm brutal-press rounded-full px-3 py-1 text-xs font-bold bg-brand-blue text-white ml-auto flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" strokeWidth={2.5} />{tr("Verleng")}</button></Tip>
                    <Tip label={tr("Aanbod verwijderen")}><button data-testid="hoppa-delete" onClick={() => del(detail.id)} className="brutal-sm brutal-press rounded-full p-1.5 bg-white"><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button></Tip>
                  </div>
                </div>
              ) : detail.status === "beschikbaar" ? (
                <div className="space-y-2">
                  <textarea data-testid="hoppa-req-msg" value={reqMsg} onChange={(e) => setReqMsg(e.target.value)} rows={2} placeholder={tr("Bericht (optioneel)")} className="w-full brutal-sm rounded-xl px-3 py-2 font-medium outline-none" />
                  <BrutalButton data-testid="hoppa-request-btn" variant="green" className="w-full" onClick={doRequest}><Send className="w-4 h-4" strokeWidth={2.5} />{tr("Aanvragen")}</BrutalButton>
                </div>
              ) : (
                <p className="text-sm font-bold text-brand-ink/50">{tr("Dit aanbod is")} {tr(detail.status)}.</p>
              )}
              {detail.owner_id !== user.user_id && isAdmin(user) && (
                <BrutalButton data-testid="hoppa-admin-delete" variant="danger" size="sm" className="w-full" onClick={() => del(detail.id)}><Trash2 className="w-4 h-4" strokeWidth={2.5} />{tr("Verwijderen (beheer)")}</BrutalButton>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
